import React from 'react';
import {
  CarIcon,
  MoneyIcon,
  PersonIcon
} from '@/Modals/MoreDetailsModal/Cards/icons';
import {
  RequirementsList,
  SectionHeaderLarge,
  SectionHeaderSmall
} from './EVTaxCreditContent.styles';
import { CheckMarkIcon, BulletIcon } from '../Modals/MoreDetailsModal/Icons';

function CustomListItem({
  type,
  children
}: {
  type: 'checkmark' | 'bullet';
  children: React.ReactNode;
}) {
  return (
    <li className="flex gap-8">
      <div>{type === 'checkmark' ? <CheckMarkIcon /> : <BulletIcon />}</div>
      {children}
    </li>
  );
}

type OwnProps = {
  headerTag: React.ReactNode;
  qualificationCopy: string;
  showAdditionalInfo: boolean;
};

export default function EVTaxCreditContent({
  headerTag,
  qualificationCopy,
  showAdditionalInfo
}: OwnProps) {
  return (
    <div className="flex flex-col gap-16">
      {headerTag}
      <section>
        <SectionHeaderLarge>Up to $4,000 in savings</SectionHeaderLarge>
        <p className="text-grey-5 text-sm">{qualificationCopy}</p>
      </section>
      {showAdditionalInfo && (
        <>
          <section className="bg-yellow-1 flex flex-col gap-16 p-16 rounded-md">
            <SectionHeaderSmall>
              <MoneyIcon /> Income Requirements
            </SectionHeaderSmall>
            <p className="t-body-s text-blue-6">
              Your modified adjusted gross income for the year in which you
              purchase the vehicle or the prior year must be less than: (i)
              $150,000 if you’re married filing jointly, (ii) $112,500 if you’re
              filing as head of household, or (iii) $75,000 for all other filers
              (including individuals, and if you’re married filing separately)
            </p>
          </section>

          <section className="bg-grey-1 flex flex-col gap-16 p-16 rounded-md">
            <SectionHeaderSmall>
              <CarIcon /> Vehicle Requirements
            </SectionHeaderSmall>
            <RequirementsList>
              <CustomListItem type={'checkmark'}>
                Chargeable battery with a capacity of at least 7 kW/h
              </CustomListItem>
              <CustomListItem type={'checkmark'}>
                Total purchase price must be less than or equal to $25,000,
                inclusive of all shipping fees, delivery charges, and applied
                discounts. Tax, title, license, and vehicle trade equity are not
                included in the total purchase price calculation.
              </CustomListItem>
              <CustomListItem type={'checkmark'}>
                At least 2 model years old
              </CustomListItem>
            </RequirementsList>
          </section>

          <section className="bg-grey-1 flex flex-col gap-16 p-16 rounded-md">
            <SectionHeaderSmall>
              <PersonIcon /> Additional Requirements
            </SectionHeaderSmall>
            <RequirementsList>
              <CustomListItem type={'bullet'}>
                No previously-owned clean vehicle tax credit has been claimed on
                this vehicle by a prior owner
              </CustomListItem>
              <CustomListItem type={'bullet'}>
                You have not claimed the previously-owned clean vehicle tax
                credit in any of the prior 3 years
              </CustomListItem>
              <CustomListItem type={'bullet'}>
                You will not be claimed as a dependent on another person’s tax
                return for the tax year in which you purchase the vehicle
              </CustomListItem>
              <CustomListItem type={'bullet'}>
                You are not purchasing the vehicle for resale or business
                purposes
              </CustomListItem>
              <CustomListItem type={'bullet'}>
                <div>
                  For full eligibility requirements and more information on the
                  previously-owned clean vehicle tax credit, please visit{' '}
                  <span>
                    <a
                      href="https://www.irs.gov/credits-deductions/used-clean-vehicle-credit"
                      className="text-grey-5 underline"
                      data-analytics-id="EV Credit Modal - IRS Link"
                    >
                      www.irs.gov/credits-deductions/used-clean-vehicle-credit
                    </a>
                  </span>
                </div>
              </CustomListItem>
            </RequirementsList>
          </section>
        </>
      )}
    </div>
  );
}
